import {
  createDefaultSettings,
  type QuestionnaireFormData,
  type QuestionnaireSchema,
  type QuestionnaireType,
  type Question,
  type Section,
  type SingleChoiceQuestion,
} from './types';
import { numberToLetterId } from './utils';

function stripLegacySettings(raw: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  const { lottery_enabled: _l1, lottery_settings: _l2, ...rest } = raw as Record<string, unknown>;
  return rest;
}

export function formDataToSchema(formData: QuestionnaireFormData): QuestionnaireSchema {
  return {
    version: '1.0',
    settings: { ...formData.settings },
    sections: formData.useSections
      ? formData.sections.map((section) => ({
          type: 'section' as const,
          id: section.id,
          name: section.name,
          description: section.description,
          questions: section.questions,
        }))
      : formData.questions,
  };
}

function isSchemaSection(item: unknown): item is Section {
  return typeof item === 'object' && item !== null && 'type' in item && (item as Section).type === 'section';
}

export function schemaToFormData(
  schema: QuestionnaireSchema | Record<string, unknown> | null | undefined,
  title: string,
  description?: string,
  type?: QuestionnaireType
): QuestionnaireFormData {
  const defaultSettings = createDefaultSettings();

  if (!schema || typeof schema !== 'object') {
    return {
      title,
      description,
      type: type ?? 'survey',
      isShared: false,
      useSections: false,
      sections: [],
      questions: [],
      settings: defaultSettings,
    };
  }

  const questionnaireSchema = schema as QuestionnaireSchema;
  const mergedSettings = {
    ...defaultSettings,
    ...stripLegacySettings(questionnaireSchema.settings as unknown as Record<string, unknown>),
  } as QuestionnaireFormData['settings'];

  const formData: QuestionnaireFormData = {
    title,
    description,
    type: type ?? 'survey',
    isShared: false,
    useSections: false,
    sections: [],
    questions: [],
    settings: mergedSettings,
  };

  const sections = questionnaireSchema.sections || [];
  const hasSections = sections.some((item) => isSchemaSection(item));

  /** 编辑态从服务端载入的 schema 已带 Hashid，不可再按序号覆盖，否则 applyStructurePatch 会收到非法 id（如 "1"）导致 Zod 校验失败 */
  const normalizeQuestion = (q: Question, qIndex: number): Question => {
    const rawId = q.id != null ? String(q.id).trim() : '';
    const question = { ...q, id: rawId !== '' ? rawId : String(qIndex + 1) } as Question;
    if ('options' in question && Array.isArray(question.options)) {
      (question as SingleChoiceQuestion).options = question.options.map((opt, optIndex) => {
        const oid = opt.id != null ? String(opt.id).trim() : '';
        return {
          ...opt,
          id: oid !== '' ? oid : numberToLetterId(optIndex + 1),
        };
      });
    }
    return question;
  };

  if (hasSections) {
    formData.useSections = true;
    formData.sections = sections
      .filter((item): item is Section => isSchemaSection(item))
      .map((section, index) => {
        const rawSid = section.id != null ? String(section.id).trim() : '';
        const sectionId = rawSid !== '' ? rawSid : String(index + 1);
        const questions = (section.questions || []).map((q, qIndex) => normalizeQuestion(q as Question, qIndex));
        return {
          type: 'section' as const,
          id: sectionId,
          name: section.name || '',
          description: section.description,
          questions,
        };
      });
  } else {
    formData.useSections = false;
    formData.questions = sections
      .filter((item): item is Question => typeof item === 'object' && item !== null && !isSchemaSection(item))
      .map((q, index) => normalizeQuestion(q as Question, index));
  }

  return formData;
}
